import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import isEmpty from 'lodash.isempty';
import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getEntitlementList} from 'src/modules/entitlements/utils/getEntitlementList';
import {getAccountEntitlementRelations} from 'src/openapi/admin/apis/AccountEntitlementsApi';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {ResourceType} from 'src/openapi/admin/models/ResourceType';
import {useAppDispatch} from 'src/store/hooks';

import {accountDatasetEntitlementsStateSelector} from '../slices/account-dataset-entitlements-state.slice';
import {accountDatasetEntitlementsActions, accountDatasetEntitlementsDataSelector, accountDatasetEntitlementsResultSelector} from '../slices/account-dataset-entitlements.slice';

import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementsFilters} from 'src/modules/entitlements/typedef';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {RelationPage} from 'src/openapi/admin/models/RelationPage';
import type {Role} from 'src/openapi/admin/models/Role';

type GetDatasetEntitlementsCacheKeys = CacheKeys<undefined, undefined, EntitlementsFilters>;

type Props = {
    accountId: number,
};

export const useGetDatasetEntitlements = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetDatasetEntitlementsCacheKeys) => apiDispatch(
        getAccountEntitlementRelations({
            accountId,
            resourceType: ResourceType.DATASET,
            pageNumber: cacheKeys.pagination.currentPage,
            pageSize: cacheKeys.pagination.pageSize,
            sortDirection: cacheKeys.sortCriteria.direction,
            requestBody: cacheKeys.filters?.resourceIds as (string[] | undefined),
        }),
    ), [accountId, apiDispatch]);

    const setLoadedData = useCallback(async (
        response: RelationPage,
        cacheKeys: GetDatasetEntitlementsCacheKeys,
    ) => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        const relations = filteredResources && !filteredResources.length ? [] : response.itemList || [];

        let roles: Record<string, Role> = {};
        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getRoleNamesByIds({requestBody: roleIds}));
        if (rolesResponse.payload.success) {
            roles = rolesResponse.payload.data;
        } else {
            dispatch(accountDatasetEntitlementsActions.onFailure(rolesResponse.payload.data));
            return;
        }

        let grants: Record<string, Grant> = {};
        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getGrantsByIds({requestBody: grantIds}));
        if (grantsResponse.payload.success) {
            grants = grantsResponse.payload.data;
        } else {
            dispatch(accountDatasetEntitlementsActions.onFailure(grantsResponse.payload.data));
            return;
        }

        let resources: Record<string, EntitlementResource> = {};
        const resourceIds = [...new Set(relations
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(getResourcesByIds({requestBody: resourceIds}));
        if (resourcesResponse.payload.success) {
            resources = resourcesResponse.payload.data;
        } else {
            dispatch(accountDatasetEntitlementsActions.onFailure(resourcesResponse.payload.data));
            return;
        }

        const data = relations.length && !isEmpty(grants) && !isEmpty(resources)
            ? getEntitlementList({
                    roles,
                    grants,
                    resources,
                    relations,
                    sortDirection: cacheKeys.sortCriteria.direction,
                }) : [];

        dispatch(accountDatasetEntitlementsActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data,
        }));
    }, [apiDispatch, dispatch]);

    return useCachedTable({
        dataSelector: accountDatasetEntitlementsDataSelector,
        stateSelector: accountDatasetEntitlementsStateSelector,
        resultSelector: accountDatasetEntitlementsResultSelector,
        actions: accountDatasetEntitlementsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
